import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  CfFtpInfoPrivileges,
} from 'data/cfFtpInfo/cfFtpInfo.types';
import * as actions from 'data/cfFtpInfo/cfFtpInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import { AppToaster } from 'shared/toaster';
import DistributorGrid from './components/CfFtpInfoGrid';
import { distributorColumnDefs } from '../../CfFtpInfo.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getCfFtpInfoList(state),
  selectedCfFtpInfo: selectors.getSelectedCfFtpInfo(state),
});

const mapDispatch = {
  goToCfFtpInfoCreate: actions.goToCfFtpInfoCreate,
  goToCfFtpInfoEdit: actions.goToCfFtpInfoEdit,
  goToCfFtpInfoView: actions.goToCfFtpInfoView,
  selectCfFtpInfo: actions.selectCfFtpInfo,
  getCfFtpInfoList: actions.getCfFtpInfoList,
  deleteCfFtpInfo: actions.deleteCfFtpInfo,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const CfFtpInfoList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedCfFtpInfo,
  goToCfFtpInfoCreate,
  goToCfFtpInfoEdit,
  goToCfFtpInfoView,
  selectCfFtpInfo,
  getCfFtpInfoList,
  deleteCfFtpInfo,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(distributorColumnDefs);

  useEffect(() => {
    getCfFtpInfoList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getCfFtpInfoList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getCfFtpInfoList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToCfFtpInfoCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectCfFtpInfo(event.api.getSelectedRows());
  };

  const handleDeleteCfFtpInfo = () => {
    const distributorConfigrationId = selectedCfFtpInfo[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedCfFtpInfo[0]?.FTPID,
        AuditService.SECURITY_INFO,
        AuditActionType.SECURITY_INFO_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteCfFtpInfo(deliveryFunctionId, distributorConfigrationId, body);
  };

  const handleGoToEdit = () => goToCfFtpInfoEdit(history);

  const handleGoToView = () => goToCfFtpInfoView(history);

  const hasPrivilegeToView = privileges.includes(CfFtpInfoPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(CfFtpInfoPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(CfFtpInfoPrivileges.Delete);

  return (
    <Fragment>
      <DistributorGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedCfFtpInfo={selectedCfFtpInfo[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteCfFtpInfo}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(CfFtpInfoList));
